/**
 * Change Management List Page
 * Displays all change requests with filters and search
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Add, 
  SearchNormal1, 
  Filter, 
  ArrowLeft,
  Clock,
  Calendar,
  User,
  DocumentText,
  RefreshCircle,
  ArrowDown2,
  Eye,
  Edit,
  Trash,
  DocumentDownload
} from 'iconsax-react';
import { 
  getChanges, 
  getTeams,
  ChangeRequest, 
  Team,
  ChangeStatus,
  ChangeCategory,
  ChangeType,
  RiskLevel,
  getStatusColor,
  getStatusLabel,
  getRiskColor,
  getCategoryIcon,
  getTypeColor,
  deleteChange,
  exportChangesToCSV
} from '../../services/changeManagementApi';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const ChangesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [changes, setChanges] = useState<ChangeRequest[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [categoryFilter, setCategoryFilter] = useState<string>(searchParams.get('category') || '');
  const [teamFilter, setTeamFilter] = useState<string>(searchParams.get('team') || '');
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('change_type') || '');
  const [riskFilter, setRiskFilter] = useState<string>(searchParams.get('risk_level') || '');
  const [showFilters, setShowFilters] = useState(false);

  const fetchChanges = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: '-created_at'
      };
      
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;
      if (teamFilter) filters.team = teamFilter;
      if (typeFilter) filters.change_type = typeFilter;
      if (riskFilter) filters.risk_level = riskFilter;
      
      const result = await getChanges(filters);
      setChanges(result.changes);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Error fetching changes:', error);
      toast.error('Failed to load changes');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, categoryFilter, teamFilter, typeFilter, riskFilter]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const result = await getTeams();
        setTeams(result);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };
    fetchTeams();
  }, []);

  const handleDelete = async (change: ChangeRequest) => {
    if (change.status !== 'draft') {
      toast.error('Only draft changes can be deleted');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${change.change_id}?`)) {
      try {
        await deleteChange(change.change_id);
        toast.success('Change deleted successfully');
        fetchChanges();
      } catch (error) {
        toast.error('Failed to delete change');
      }
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setTeamFilter('');
    setTypeFilter('');
    setRiskFilter('');
    setSearchParams({});
  };

  const filteredChanges = changes.filter(change => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      change.change_id.toLowerCase().includes(search) ||
      change.title.toLowerCase().includes(search) ||
      (change.description && change.description.toLowerCase().includes(search)) ||
      (change.requester_name && change.requester_name.toLowerCase().includes(search))
    );
  });

  const statusOptions: ChangeStatus[] = [
    'draft', 'submitted', 'under_review', 'approved', 'rejected',
    'scheduled', 'in_progress', 'completed', 'failed', 'rolled_back', 'cancelled', 'closed'
  ];

  const categoryOptions: ChangeCategory[] = [
    'infrastructure', 'rule', 'integration', 'security', 'network', 'application', 'configuration', 'other'
  ];

  const typeOptions: ChangeType[] = ['normal', 'standard', 'emergency'];
  const riskOptions: RiskLevel[] = ['low', 'medium', 'high', 'critical'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/change-management')}
            className="p-2 rounded-lg bg-background-card border border-border-dark hover:bg-background-hover transition-colors"
            aria-label="Back to Change Management Dashboard"
          >
            <ArrowLeft size={20} color="#9CA3AF" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Change Requests</h1>
            <p className="text-gray-400 mt-1">
              {pagination.total} total changes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Export Button */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportChangesToCSV(changes, 'change-requests')}
              disabled={changes.length === 0}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-card border border-border-dark text-gray-300 hover:border-green-500 hover:text-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to CSV"
            >
              <DocumentDownload size={18} color="#D1D5DB" />
              <span>CSV</span>
            </button>
          </div>
          <button
            onClick={() => navigate('/changes/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary rounded-lg text-white hover:bg-primary/80 transition-colors"
          >
            <Add size={20} color="#FFFFFF" />
            <span>New Change</span>
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-background-card rounded-xl p-4 border border-border-dark">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[300px]">
            <SearchNormal1 
              size={20} 
              color="#9CA3AF" 
              className="absolute left-3 top-1/2 -translate-y-1/2" 
            />
            <input
              type="text"
              placeholder="Search by ID, title, or requester..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters ? 'bg-primary text-white border-primary' : 'bg-background-hover border-border-dark text-gray-300 hover:border-primary'
            }`}
          >
            <Filter size={20} color={showFilters ? "#FFFFFF" : "#9CA3AF"} />
            <span>Filters</span>
            {(statusFilter || categoryFilter || teamFilter || typeFilter || riskFilter) && (
              <span className="w-2 h-2 rounded-full bg-primary"></span>
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={fetchChanges}
            className="p-2 rounded-lg bg-background-hover border border-border-dark hover:border-primary transition-colors"
            title="Refresh"
          >
            <RefreshCircle size={20} color="#9CA3AF" />
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border-dark">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Status */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{getStatusLabel(status)}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  <option value="">All Categories</option>
                  {categoryOptions.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Team */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Team</label>
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  <option value="">All Teams</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  <option value="">All Types</option>
                  {typeOptions.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Risk */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Risk Level</label>
                <select
                  value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                >
                  <option value="">All Risk Levels</option>
                  {riskOptions.map(risk => (
                    <option key={risk} value={risk}>{risk.charAt(0).toUpperCase() + risk.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Clear Filters */}
            {(statusFilter || categoryFilter || teamFilter || typeFilter || riskFilter) && (
              <button
                onClick={clearFilters}
                className="mt-3 text-primary text-sm hover:underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Changes Table */}
      <div className="bg-background-card rounded-xl border border-border-dark overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredChanges.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <DocumentText size={48} color="#4B5563" className="mb-4" />
            <p>No changes found</p>
            <button
              onClick={() => navigate('/changes/new')}
              className="mt-4 text-primary hover:underline"
            >
              Create your first change request
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-hover">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Change ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Risk
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Regions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-dark">
                {filteredChanges.map((change) => (
                  <tr 
                    key={change.id}
                    className="hover:bg-background-hover transition-colors cursor-pointer"
                    onClick={() => navigate(`/changes/${change.change_id}`)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-primary font-mono text-sm">{change.change_id}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-xs">
                        <p className="text-white font-medium truncate">{change.title}</p>
                        <p className="text-gray-500 text-xs truncate">{change.requester_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getTypeColor(change.change_type)}`}>
                        {change.change_type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{getCategoryIcon(change.category)}</span>
                        <span className="text-gray-300 text-sm capitalize">{change.category}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(change.status)}`}>
                        {getStatusLabel(change.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getRiskColor(change.risk_level)}`}>
                        {change.risk_level}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-300 text-sm">
                      {change.team_name || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {change.regions && change.regions.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {change.regions.map((region: string, idx: number) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary border border-primary/30"
                            >
                              {region}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-400 text-sm">
                      {change.created_at ? new Date(change.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/changes/${change.change_id}`)}
                          className="p-2 rounded-lg hover:bg-background-hover text-gray-400 hover:text-white transition-colors"
                          title="View"
                        >
                          <Eye size={18} color="#9CA3AF" />
                        </button>
                        {(() => {
                          // Compare as strings to handle any type mismatches (ObjectId vs string, etc.)
                          // Also trim whitespace and handle null/undefined cases
                          const user_id = user?.id ? String(user.id).trim() : null;
                          const requested_by = change?.requested_by ? String(change.requested_by).trim() : null;
                          const isCreator = user_id && requested_by && user_id === requested_by;
                          const canEdit = isCreator && ['draft', 'rejected', 'submitted', 'under_review'].includes(change.status);
                          const canDelete = change.status === 'draft' && isCreator;
                          
                          return (
                            <>
                              {canEdit && (
                                <button
                                  onClick={() => navigate(`/changes/${change.change_id}/edit`)}
                                  className="p-2 rounded-lg hover:bg-background-hover text-gray-400 hover:text-primary transition-colors"
                                  title="Edit"
                                >
                                  <Edit size={18} color="#9CA3AF" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDelete(change)}
                                  className="p-2 rounded-lg hover:bg-background-hover text-gray-400 hover:text-red-500 transition-colors"
                                  title="Delete"
                                >
                                  <Trash size={18} color="#9CA3AF" />
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-dark">
            <p className="text-sm text-gray-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 rounded-lg bg-background-hover text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/20"
              >
                Previous
              </button>
              <span className="text-gray-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 rounded-lg bg-background-hover text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/20"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangesListPage;
